import { Button, Divider, Flex, Input, Segmented, Select, Tooltip } from 'antd'
import {
  Download,
  LayoutGrid,
  ListTree,
  Plus,
  Search,
  Table2
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { CardToolbarRow } from 'src/components'

import type { OrgTypeFilter, OrgView } from '../types'

type Props = {
  view: OrgView
  onViewChange: (v: OrgView) => void
  search: string
  onSearchChange: (v: string) => void
  typeFilter: OrgTypeFilter
  onTypeFilterChange: (v: OrgTypeFilter) => void
  onExport: () => void
  /** Omitted when the viewer may not change the structure. */
  onCreate?: () => void
  exportDisabled?: boolean
}

export const OrgToolbar = ({
  view,
  onViewChange,
  search,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  onExport,
  onCreate,
  exportDisabled
}: Props) => {
  const { t } = useTranslation()

  return (
    <CardToolbarRow
      left={
        <>
          <Input
            size="small"
            prefix={<Search size={14} />}
            placeholder={t('organization.toolbar.searchPlaceholder')}
            allowClear
            style={{ maxWidth: 320, flex: '1 1 220px' }}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          <Select<OrgTypeFilter>
            size="small"
            allowClear
            placeholder={t('organization.toolbar.types.all')}
            value={typeFilter === 'all' ? undefined : typeFilter}
            onChange={(value) => onTypeFilterChange(value ?? 'all')}
            style={{ width: 150 }}
            options={[
              {
                value: 'division',
                label: t('organization.toolbar.types.divisions')
              },
              {
                value: 'department',
                label: t('organization.toolbar.types.departments')
              },
              { value: 'team', label: t('organization.toolbar.types.teams') }
            ]}
          />
        </>
      }
      right={
        <>
          <Segmented
            size="small"
            value={view}
            onChange={(v) => onViewChange(v as OrgView)}
            options={[
              {
                label: (
                  <Flex align="center" gap={6}>
                    <LayoutGrid size={14} />
                    <span>{t('organization.views.chart')}</span>
                  </Flex>
                ),
                value: 'chart'
              },
              {
                label: (
                  <Flex align="center" gap={6}>
                    <ListTree size={14} />
                    <span>{t('organization.views.tree')}</span>
                  </Flex>
                ),
                value: 'tree'
              },
              {
                label: (
                  <Flex align="center" gap={6}>
                    <Table2 size={14} />
                    <span>{t('organization.views.browse')}</span>
                  </Flex>
                ),
                value: 'browse'
              }
            ]}
          />
          <Divider type="vertical" style={{ margin: 0 }} />
          <Tooltip title={t('organization.notApiBacked.exportTooltip')}>
            <Button
              size="small"
              icon={<Download size={14} />}
              onClick={onExport}
              disabled={exportDisabled}
            >
              {t('organization.actions.export')}
            </Button>
          </Tooltip>
          {onCreate ? (
            <Button
              size="small"
              type="primary"
              icon={<Plus size={14} />}
              onClick={onCreate}
            >
              {t('organization.actions.newDivision')}
            </Button>
          ) : null}
        </>
      }
    />
  )
}
